import {useEffect} from "react";
import {getConstant} from "./constantsTagsUtils";
import {intrinsics} from "../components/dialogs/project/ConstantStyle";
import {Constants, Tags} from "../components/graph/StateTags";

interface StyleSettings {
    bold: boolean,
    italic: boolean,
    underline: boolean,
    strike: boolean,
    backColor: string | null,
    textColor: string | null
}
export interface StyleOptions extends StyleSettings
{
    header: StyleSettings
    body: StyleSettings
    footer: StyleSettings
}

export function getInitialStyle()
{
    const defaultStyle = {
        backColor: null,
        textColor: null,
        bold: false,
        italic: false,
        strike: false,
        underline: false
    };
    
    return {
        ...defaultStyle,
        header: {...defaultStyle},
        body: {...defaultStyle},
        footer: {...defaultStyle}
    };
}

export type StyleTargets = "header" | "body" | "footer";

interface Props
{
    name: string
    style: StyleOptions
}

export default function Style({ name, style }: Props)
{
    useEffect(() =>
    {
        const appendStyle = (name: string, style: StyleSettings) => {
            const element = document.createElement("style");

            element.id = name;
            element.innerHTML = `.style${name}, .style${name} p { 
                font-weight: ${style.bold ? "bold" : ""}; font-style: ${style.italic ? "italic" : ""};
                text-decoration: ${style.strike ? "line-through" : ""} ${style.underline ? "underline" : ""};
                color: ${style.textColor}; background-color: ${style.backColor};}`
            document.head.appendChild(element);
            return element;
        };

        const baseStyle = appendStyle(name, style);
        const headerStyle = style.header && appendStyle("_header_" + name, style.header);
        const bodyStyle = style.body && appendStyle("_body_" + name, style.body);
        const footerStyle = style.footer && appendStyle("_footer_" + name, style.footer);
        return () =>
        {
            baseStyle.remove();
            headerStyle?.remove();
            bodyStyle?.remove();
            footerStyle?.remove();
        }
    }, [name, style]);

    return null;
}

export function getStyles(state: string, tags: Tags, blockNames: string [], constants: Constants, target: StyleTargets) {
    const styles: string[] = [];
    const testIntrinsic = (intrinsic: string, value: string) => {
        switch (intrinsic) {
            case "ulid":
                return value === state;
            case "blockName":
                return (blockNames).find(blockName => blockName.includes(value));
        }
        return false;
    }

    Object.values(tags[state] ?? {})
        .forEach((tag) =>
        {
            const constant = getConstant(tag.type, constants);
            
            if (constant?.type === "date") {
                Object.entries(constant.style || {}).forEach(([date, style]) => {
                    if (Number(date) <= Number(tag.value)) {
                        styles.push(`style${tag.type}_${date}`);
                        if (style[target]) {
                            styles.push(`style_${target}_${tag.type}_${date}`);
                        }
                    }
                })
            } else {
                const style = constant?.style && constant.style[tag.value as string];

                if (style) {
                    styles.push(`style${tag.type}_${tag.value}`);
                    if (style![target]) {
                        styles.push(`style_${target}_${tag.type}_${tag.value}`);
                    }
                }
            }
        });
    Object.entries(constants || {})
        .filter(([intrinsic]) => intrinsics[intrinsic])
        .forEach(([intrinsic, constant]) =>
            Object.keys(constant.style ?? {}).filter(value => testIntrinsic(intrinsic, value))
                .forEach(value => {
                    styles.push(`style${intrinsic}_${value}`)
                    if (constant.style![value][target]) {
                        styles.push(`style_${target}_${intrinsic}_${value}`)
                    }
                })
        );
    return styles.join(" ");
}